import { useCallback } from "react"
import { loadStripe } from "@stripe/stripe-js"
import { Link, useNavigate } from "@tanstack/react-router"
import { IconCircleArrowUpOutline18 as ArrowUpCircle } from "nucleo-ui-outline-18"

import type { PaidPlan } from "@productlane/lib/src/browser"
import { suggestedOverageLimitCents } from "@productlane/lib"
import { Button } from "@productlane/ui"

import { toast } from "@/components/toast"
import { env } from "@/env"
import { api } from "@/lib/api"
import { getPlanGateAction } from "@/lib/plan-gate"
import { useTrial } from "@/lib/useTrial"
import { SETTINGS_CARD_NOTICE_CLASS } from "../settings-switches/settings-container"
import { AI_LIMIT_FIELD_HASH } from "./ai-limit-focus"

export type AiPausedReason = "cap" | "trial" | "no-plan"

const TRIAL_PLAN: PaidPlan = "pro"

const usd = (value: number) => `$${value.toFixed(2)}`

// A link button draws no box, so its min-height only stretches the strip.
const FLAT = "h-auto min-h-0"

// The limit that starts the agent again in one click: high enough to clear what
// the workspace already spent, so nobody has to work out a number.
export function nextLimitUsd({
	spentUsd,
	allowanceUsd,
}: {
	spentUsd: number
	allowanceUsd: number
}): number {
	const overageUsedCents = Math.max(
		0,
		Math.round((spentUsd - allowanceUsd) * 100),
	)
	return suggestedOverageLimitCents(overageUsedCents) / 100
}

export function aiPausedBody({
	reason,
	spentUsd,
	pausePointUsd,
}: {
	reason: AiPausedReason
	spentUsd: number
	pausePointUsd: number | null
}): string {
	if (reason === "trial") {
		return `Your trial's AI credit is spent at ${usd(spentUsd)}.`
	}
	if (reason === "no-plan") {
		return "Your AI agent stopped answering. Paid AI runs on the Starter plan and up."
	}
	return pausePointUsd === null
		? `AI generation is paused. Spend reached ${usd(spentUsd)}.`
		: `AI generation is paused. Spend reached ${usd(spentUsd)} of your ${usd(pausePointUsd)} limit.`
}

// A strip across the top of the card it belongs to, so the reason the agent
// went quiet sits on the settings that caused it.
export function AiPausedBanner() {
	const navigate = useNavigate()
	const ctx = api.useUtils()
	const { isTrial, hadRecentCancellation, billingInterval } = useTrial()
	const { data } = api.workspace.getAiBudgetStatus.useQuery(undefined, {
		staleTime: 30_000,
	})

	const raiseLimit = api.workspace.updateAiTokenCap.useMutation()
	const trialMutation = api.workspace.createTrialSubscription.useMutation({
		onError: (error) => {
			toast.error({
				title: "The trial could not be started",
				description: error.message,
			})
		},
	})
	const updatePayment = api.workspace.updatePayment.useMutation()

	const openStripe = useCallback(async (idOrUrl: string) => {
		if (idOrUrl.startsWith("http")) {
			location.href = idOrUrl
			return
		}
		const stripe = await loadStripe(env.VITE_STRIPE_PUBLISHABLE_KEY)
		await stripe?.redirectToCheckout({ sessionId: idOrUrl })
	}, [])

	const addPaymentMethod = useCallback(async () => {
		try {
			const sessionId = await updatePayment.mutateAsync()
			if (sessionId) await openStripe(sessionId)
		} catch {
			toast.error({
				title: "Couldn't open the payment form",
				description: "Add your card from Settings, Plans instead.",
			})
		}
	}, [openStripe, updatePayment])

	const startTrial = useCallback(async () => {
		const result = await trialMutation.mutateAsync({
			plan: TRIAL_PLAN,
			interval: billingInterval,
		})
		if (result) await openStripe(result)
	}, [billingInterval, openStripe, trialMutation])

	const raiseTo = useCallback(
		(limitUsd: number) => {
			raiseLimit.mutate(
				{ capCents: Math.round(limitUsd * 100) },
				{
					onSuccess: () => {
						void ctx.workspace.getAiBudgetStatus.invalidate()
						void ctx.workspace.getAiTokenUsage.invalidate()
						toast.success({
							title: `AI is running again, with a ${usd(limitUsd)} limit`,
						})
					},
					onError: () => {
						toast.error({ title: "Couldn't raise the AI spend limit" })
					},
				},
			)
		},
		[ctx, raiseLimit],
	)

	if (!data?.blocked) return null

	const reason = data.reason as AiPausedReason
	const body = aiPausedBody({
		reason,
		spentUsd: data.spentUsd,
		pausePointUsd: data.pausePointUsd,
	})
	const limitUsd = nextLimitUsd({
		spentUsd: data.spentUsd,
		allowanceUsd: data.allowanceUsd,
	})
	// A workspace that already trialed, or cancelled in the last two weeks,
	// cannot start another one, so it gets the plans page instead.
	const planAction = getPlanGateAction({
		hasRequiredPlan: false,
		initialized: true,
		isTrial,
		hadRecentCancellation,
	})

	return (
		<div
			className={`${SETTINGS_CARD_NOTICE_CLASS} flex select-none flex-wrap items-center gap-x-3 gap-y-1 bg-amber-50/70 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400`}
		>
			<span className="flex min-w-0 items-center gap-2">
				<span className="h-1.5 w-1.5 flex-none rounded-full bg-amber-500" />
				{body}
			</span>
			<span className="ml-auto flex flex-none items-center gap-3">
				{reason === "cap" && (
					<>
						<Button
							variant="link"
							size="sm"
							className={FLAT}
							onClick={() =>
								void navigate({
									href: `/settings/ai/usage${AI_LIMIT_FIELD_HASH}`,
								})
							}
						>
							Set another amount
						</Button>
						<Button
							variant="link"
							size="sm"
							className={`${FLAT} text-primary underline underline-offset-2`}
							isLoading={raiseLimit.isLoading}
							onClick={() => raiseTo(limitUsd)}
						>
							Raise to {usd(limitUsd)}
						</Button>
					</>
				)}
				{reason === "trial" && (
					<Button
						variant="link"
						size="sm"
						className={`${FLAT} text-primary underline underline-offset-2`}
						isLoading={updatePayment.isLoading}
						onClick={() => void addPaymentMethod()}
					>
						Add payment details
					</Button>
				)}
				{reason === "no-plan" && (
					<>
						<Link to="/settings/plans">
							<Button variant="link" size="sm" className={FLAT}>
								See plans
							</Button>
						</Link>
						{planAction === "start-trial" ? (
							<Button
								variant="link"
								size="sm"
								Icon={ArrowUpCircle}
								className={`${FLAT} text-primary underline underline-offset-2`}
								isLoading={trialMutation.isLoading}
								onClick={() => void startTrial()}
							>
								Start 7-day trial
							</Button>
						) : (
							<Button
								variant="link"
								size="sm"
								className={`${FLAT} text-primary underline underline-offset-2`}
								isLoading={updatePayment.isLoading}
								onClick={() => void addPaymentMethod()}
							>
								Add payment details
							</Button>
						)}
					</>
				)}
			</span>
		</div>
	)
}
